// src/pages/Services.js

import React, { useReducer, useEffect } from 'react';
import {
    Table,
    TableHead,
    TableBody,
    Paper,
    Typography,
    CircularProgress,
    Toolbar as MuiToolbar,
} from '@mui/material';
import ServiceRow from './ServiceRow'; // Import your ServiceRow component
import Pagination from '../components/Pagination'; // Import your ServicePagination component
import ServicesTableHead from '../components/ServicesTableHead'; // Import your table head for services
import { ACTIONS } from '../reducers/listActions';
import { initialState, servicesReducer } from '../reducers/servicesReducer'; // Import services reducer
import { handleSortRequest } from "../utils/sortingUtils";
import api from "../services/api";

// Define columns for the services table
const columns = [
    { id: 'redirect', label: '', sortable: false }, // For Redirect Icon
    { id: 'date', label: 'Date', sortable: true },
    { id: 'time', label: 'Time', sortable: false },
    { id: 'service', label: 'Service', sortable: false },
    { id: 'amount', label: 'Amount', sortable: false },
    { id: 'customer', label: 'Customer', sortable: false },
    { id: 'pet', label: 'Pet', sortable: false },
    { id: 'groomer', label: 'Groomer', sortable: false },
    { id: 'comments', label: '', sortable: false },
    { id: 'imageUrl', label: '', sortable: false },
];

function Services({ data }) {
    const [state, dispatch] = useReducer(servicesReducer, initialState);
    const {
        services,
        loading,
        error,
        order,
        orderBy,
        page,
        rowsPerPage,
        totalServices,
    } = state;

    const isSearchMode = !!data;
    const displayedServices = isSearchMode ? data : services;

    useEffect(() => {
        if (isSearchMode) return;

        const fetchServices = async () => {
            dispatch({ type: ACTIONS.FETCH_INIT });
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    throw new Error('No token found');
                }
                //console.log(`🚀 API Call: sortBy=${orderBy}, order=${order}`);
                const response = await api.get('/services', {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        page: page + 1, // Adjust if your backend uses 1-based indexing
                        limit: rowsPerPage,
                        sortBy: orderBy,
                        order: order,
                    },
                });
                //console.log("🚀 Received Sorted Data:", response.data.data.map(s => s.date));
                dispatch({
                    type: ACTIONS.FETCH_SUCCESS,
                    payload: {
                        services: response.data.data, // Adjust according to your API response structure
                        total: response.data.total,
                    },
                });
            } catch (err) {
                console.error('Error fetching services:', err);
                dispatch({ type: ACTIONS.FETCH_FAILURE, payload: 'Failed to fetch services.' });
            }
        };

        fetchServices();
    }, [page, rowsPerPage, order, orderBy, isSearchMode]);

    const handleSort = handleSortRequest(orderBy, order, dispatch);

    const handleChangePage = (event, newPage) => {
        dispatch({ type: ACTIONS.SET_PAGE, payload: newPage });
    };

    const handleChangeRowsPerPage = (event) => {
        dispatch({ type: ACTIONS.SET_ROWS_PER_PAGE, payload: parseInt(event.target.value, 10) });
    };

    return (
        <>
            <MuiToolbar />
            <Paper sx={{ padding: '1rem', width: '97%', margin: '1rem auto' }}>
                <Typography variant="h4" gutterBottom>
                    Services
                </Typography>
                {loading ? (
                    <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                        <CircularProgress />
                    </div>
                ) : error ? (
                    <Typography variant="h6" color="error" align="center">
                        {error}
                    </Typography>
                ) : displayedServices.length === 0 ? (
                    <Typography>No services found.</Typography>
                ) : (
                    <>
                        {/* Top Pagination */}
                        {!isSearchMode && (
                            <Pagination
                                count={totalServices} // e.g., totalCustomers or totalServices
                                page={page}
                                rowsPerPage={rowsPerPage}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        )}
                        <Table>
                            <TableHead>
                                <ServicesTableHead
                                    columns={columns}
                                    order={order}
                                    orderBy={orderBy}
                                    onSortRequest={handleSort}
                                />
                            </TableHead>
                            <TableBody>
                                {displayedServices.map((service) => (
                                    //console.log("🖥 Rendering Service Row:", service.date),
                                    <ServiceRow key={service.id} service={service} columns={columns} services={displayedServices} />
                                ))}
                            </TableBody>
                        </Table>
                        {/* Bottom Pagination */}
                        {!isSearchMode && (
                            <Pagination
                                count={totalServices} // e.g., totalCustomers or totalServices
                                page={page}
                                rowsPerPage={rowsPerPage}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        )}
                    </>
                )}
            </Paper>
        </>
    );
}

export default Services;